import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import {
	DictionaryEntry,
	type GrammarEntry,
	type KanjiEntry,
	type VocabularyEntry,
} from "~/components/ui/dictionary-entry";
import { currentPageContextAtom } from "~/components/practice/chat-widget";
import { generateDictionaryPageContext } from "~/lib/context-gen";
import { Input } from "~/components/ui/input";
import { useDictionarySearch } from "~/lib/client/dictionary";
import type {
	GrammarContent,
	KanjiContent,
	SourceType,
	VocabularyContent,
} from "~/lib/server/db/schema/flashcard-schema";
import { m } from "~/i18n/messages";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";

export const Route = createFileRoute("/app/dictionary/")({
	component: DictionaryPage,
});

function DictionarySearchSkeleton() {
	return (
		<div className="space-y-4">
			{[1, 2, 3].map((id) => (
				<div
					key={id}
					className="border rounded-lg p-4 animate-pulse"
				>
					<div className="flex items-start justify-between mb-3">
						<div className="h-6 bg-gray-200 rounded w-32" />
						<div className="h-5 bg-gray-200 rounded w-16" />
					</div>
					<div className="space-y-2">
						<div className="h-4 bg-gray-200 rounded w-3/4" />
						<div className="h-4 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</div>
			))}
		</div>
	);
}

type RawEntry = {
	sid: string;
	gid: string | null;
	reading: string;
	content?: unknown;
	type: SourceType;
	level?: string;
};

function transformEntry(
	entry: RawEntry,
): VocabularyEntry | KanjiEntry | GrammarEntry {
	const level = entry.level ?? entry.type.toUpperCase();
	switch (entry.type) {
		case "vocabulary": {
			const content = entry.content as VocabularyContent;
			return {
				reading: entry.reading,
				level,
				content: {
					translations: content.translations,
					kanji: content.kanji,
					phrases: content.phrases,
					kanjiForms: content.kanjiForms,
					readings: content.readings,
				},
			} satisfies VocabularyEntry;
		}
		case "kanji": {
			const content = entry.content as KanjiContent;
			return {
				reading: entry.reading,
				level,
				content: {
					strokes: content.strokes,
					mnemonic: content.mnemonic,
					svg: content.svg,
					words: content.words,
					onyomi: content.onyomi,
					kunyomi: content.kunyomi,
					meanings: content.meanings,
				},
			} satisfies KanjiEntry;
		}
		case "grammar": {
			const content = entry.content as GrammarContent;
			return {
				reading: entry.reading,
				level,
				content: {
					usages: content.usages ?? [],
					meaning: {
						meaning: content.meaning.meaning,
						example: content.meaning.example,
					},
				},
			} satisfies GrammarEntry;
		}
		default:
			// fallback to vocabulary entry with minimal fields
			// this should never happen though
			return {
				reading: entry.reading,
				level,
				content: { translations: [] },
			};
	}
}

function DictionaryResults({ query }: { query: string }) {
	const { data: dictionaryEntries = [] } = useDictionarySearch(query);

	if (dictionaryEntries.length === 0) {
		return (
			<p className="text-pink-500">{m["dictionary.no_results_message"]()}</p>
		);
	}

	return (
		<div className="space-y-4">
			{dictionaryEntries.map((entry) => {
				const transformedEntry = transformEntry(entry);
				return (
					<DictionaryEntry
						key={entry.sid}
						entryType={entry.type}
						entry={transformedEntry}
						level={transformedEntry.level ?? entry.type.toUpperCase()}
					/>
				);
			})}
		</div>
	);
}

function DictionaryPage() {
	const setPageContext = useSetAtom(currentPageContextAtom);
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState(query);

	useEffect(() => {
		const handler = setTimeout(() => setDebouncedQuery(query), 300);
		return () => clearTimeout(handler);
	}, [query]);

	useEffect(() => {
		setPageContext({
			title: m["dictionary.title"](),
			slug: "/app/dictionary",
			contextData: generateDictionaryPageContext({
				query,
				resultCount: 0, // Will be updated by DictionaryResults component
			}),
		});
	}, [setPageContext, query]);

	return (
		<div className="p-4 max-w-screen-md mx-auto">
			<h1 className="text-2xl font-semibold text-pink-600">
				{m["dictionary.title"]()}
			</h1>
			<p className="text-muted-foreground mb-6 text-sm">
				{m["dictionary.search_description"]()}
				{m["dictionary.completeness_note"]()}
				{m["dictionary.recommendation"]()}{" "}
				<a
					href="https://www.jisho.org/"
					className="text-pink-600/70 hover:text-pink-600"
				>
					{m["dictionary.external_links.jisho"]()}
				</a>
			</p>
			<Input
				type="search"
				placeholder={m["dictionary.search_placeholder"]()}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				className="mb-6 border-pink-400 focus:border-pink-600 focus:ring-pink-600"
			/>
			{debouncedQuery.length > 0 && (
				<SuspenseFallback
					loading={<DictionarySearchSkeleton />}
					error={
						<p className="text-red-600">
							{m["errors.loading_dictionary_entries"]()}
						</p>
					}
				>
					<DictionaryResults query={debouncedQuery} />
				</SuspenseFallback>
			)}
		</div>
	);
}
